import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  ReactNode
} from 'react'
import {
  createTheme,
  ThemeProvider as MUIThemeProvider
} from '@mui/material/styles'
import { ThemeContextType } from '../types'

// Create context with undefined as initial value but assert the type
const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

interface ThemeProviderProps {
  children: ReactNode
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // State to track current theme mode
  const [mode, setMode] = useState<'light' | 'dark'>('light')

  // Function to toggle between light and dark themes
  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'))
  }

  // Create Material-UI theme based on current mode
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: mode === 'light' ? '#1976d2' : '#90caf9'
          },
          secondary: {
            main: mode === 'light' ? '#dc004e' : '#f48fb1'
          },
          background: {
            default: mode === 'light' ? '#f5f5f5' : '#121212',
            paper: mode === 'light' ? '#ffffff' : '#1e1e1e'
          }
        },
        typography: {
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
        }
      }),
    [mode]
  )

  // Context value that will be provided to consumers
  const contextValue: ThemeContextType = {
    mode,
    toggleTheme
  }

  return (
    <ThemeContext.Provider value={contextValue}>
      <MUIThemeProvider theme={theme}>{children}</MUIThemeProvider>
    </ThemeContext.Provider>
  )
}

// Custom hook to use theme context
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
